import { useState, useEffect } from 'react'
import { cyberpunkTheme } from '../../styles/theme'

/* ─────────────────────────────────────────
   Pixel diamond data (same as OG banner / og-banner.html)
───────────────────────────────────────── */
function getPixelDiamondRects() {
  const S = 15
  const out = []
  for (let row = -S; row <= S; row++) {
    const rowW = S - Math.abs(row)
    for (let col = -rowW; col <= rowW; col++) {
      const t = (Math.abs(col) + Math.abs(row)) / S
      const fill = t < 0.30 ? '#200045' : t < 0.70 ? '#1a0038' : '#12002a'
      out.push({ x: col, y: row, fill })
    }
  }
  for (let row = -S; row <= S; row++) {
    const rowW = S - Math.abs(row)
    for (let col = -rowW; col <= rowW; col++) {
      if (Math.abs(col) + Math.abs(row) !== S) continue
      out.push({ x: col, y: row, fill: '#9333ea' })
    }
  }
  const CR = 5
  for (let dy = -CR; dy <= CR; dy++) {
    for (let dx = -CR; dx <= CR; dx++) {
      const d = Math.sqrt(dx * dx + dy * dy)
      if (d <= CR - 1) out.push({ x: dx, y: dy, fill: '#a855f7' })
      else if (d <= CR + 0.5) out.push({ x: dx, y: dy, fill: '#e9d5ff' })
    }
  }
  out.push({ x: 0, y: 0, fill: '#ffffff' })
  return out
}

/**
 * LnsLogo — Retro Arcade animated logo
 * variant="full"   → Variation A: stacked LAST / NAD / STANDING + elim bar (Home hero)
 * variant="header" → Variation C: horizontal NAD box + text lockup (AppHeader)
 * variant="icon"   → compact square icon (future use)
 */
export default function LnsLogo({ variant = 'full', showText = true }) {
  if (variant === 'header') return <HeaderLogo />
  if (variant === 'icon')   return <IconLogo />
  return <FullLogo showText={showText} />
}

/* ─────────────────────────────────────────
   Animated number grid 1–10
───────────────────────────────────────── */
// Initial state: 3=picked(cyan), 5=danger(red), 7=picked(cyan), rest=idle
const INITIAL_STATES = [
  'idle','idle','picked','idle','danger','idle','picked','idle','idle','idle'
]

// Sequence of "round updates" to loop through — simulates live game
const ROUND_SEQUENCES = [
  ['idle','idle','picked','idle','danger','idle','picked','idle','idle','idle'],
  ['idle','picked','idle','idle','danger','picked','idle','idle','idle','idle'],
  ['idle','idle','idle','picked','idle','danger','idle','picked','idle','idle'],
  ['picked','idle','idle','idle','idle','idle','danger','idle','picked','idle'],
  ['idle','idle','picked','idle','idle','picked','idle','danger','idle','idle'],
]

function NumberGrid() {
  const [states, setStates] = useState(INITIAL_STATES)

  useEffect(() => {
    let seq = 0
    const interval = setInterval(() => {
      seq = (seq + 1) % ROUND_SEQUENCES.length
      setStates(ROUND_SEQUENCES[seq])
    }, 4400)
    return () => clearInterval(interval)
  }, [])

  // Self-contained keyframe — no dependency on parent FullLogo's <style>
  const gridCss = `@keyframes dying-flash { 0%,100%{opacity:1} 50%{opacity:0.25} }`

  const numStyle = (state) => {
    const base = {
      fontFamily: cyberpunkTheme.fonts.display,
      fontSize: '0.6rem',
      width: 'clamp(22px, 6vw, 28px)', height: 'clamp(22px, 6vw, 28px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: '3px',
      transition: 'all 0.4s ease',
      cursor: 'default',
      flexShrink: 0,
    }
    if (state === 'picked') return {
      ...base,
      border: '2px solid rgba(0, 245, 255, 0.5)',
      color: '#00f5ff',
      background: 'rgba(0, 245, 255, 0.08)',
      boxShadow: '0 0 10px rgba(0, 245, 255, 0.2)',
      textShadow: '0 0 6px rgba(0, 245, 255, 0.5)',
    }
    if (state === 'danger') return {
      ...base,
      border: '2px solid rgba(239, 68, 68, 0.5)',
      color: '#ef4444',
      background: 'rgba(239, 68, 68, 0.08)',
      boxShadow: '0 0 10px rgba(239, 68, 68, 0.2)',
      textShadow: '0 0 6px rgba(239, 68, 68, 0.5)',
      animation: 'dying-flash 4s ease-in-out infinite',
    }
    return {
      ...base,
      border: '1px solid rgba(168, 85, 247, 0.18)',
      color: '#444',
      background: 'rgba(168, 85, 247, 0.03)',
    }
  }

  return (
    <>
      <style>{gridCss}</style>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(3px, 1vw, 6px)', marginTop: 18, flexWrap: 'nowrap', overflow: 'hidden', maxWidth: '100%', padding: '0 4px' }}>
        {states.map((state, i) => (
          <div key={i} style={numStyle(state)}>{i + 1}</div>
        ))}
      </div>
    </>
  )
}

/* ─────────────────────────────────────────
   VARIATION A — Full stacked logo (Home hero)
───────────────────────────────────────── */
function FullLogo({ showText: _showText }) {
  const css = `
    @keyframes nad-pulse {
      0%, 100% {
        text-shadow:
          0 0 12px rgba(0, 245, 255, 0.9),
          0 0 35px rgba(0, 245, 255, 0.5),
          0 0 70px rgba(0, 245, 255, 0.2),
          5px 5px 0 #062a30;
      }
      50% {
        text-shadow:
          0 0 18px rgba(0, 245, 255, 1),
          0 0 55px rgba(0, 245, 255, 0.7),
          0 0 100px rgba(0, 245, 255, 0.3),
          5px 5px 0 #062a30;
      }
    }
    @keyframes nad-glitch {
      0%, 88%, 100% { opacity: 0; transform: translate(0); }
      89%  { opacity: 0.6; transform: translate(-4px, 2px);  clip-path: inset(10% 0 70% 0); }
      90%  { opacity: 0; }
      91%  { opacity: 0.5; transform: translate(3px, -1px);  clip-path: inset(60% 0 10% 0); }
      92%  { opacity: 0; }
    }
    @keyframes seg-glow {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.65; }
    }
    @keyframes dying-flash {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.25; }
    }
    @keyframes dot-blink {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.3; }
    }
    @keyframes coin-blink {
      0%, 60%  { opacity: 1; }
      61%, 100% { opacity: 0; }
    }
  `

  const segs = [
    { state: 'alive' }, { state: 'alive' }, { state: 'alive' },
    { state: 'alive' }, { state: 'alive' }, { state: 'alive' },
    { state: 'alive' }, { state: 'dying' },
    { state: 'dead'  }, { state: 'dead'  },
  ]

  const segStyle = (state) => {
    if (state === 'alive') return {
      background: 'linear-gradient(180deg, #a855f7, #7c3aed)',
      boxShadow: '0 0 10px rgba(168,85,247,0.5), 0 2px 4px rgba(0,0,0,0.3)',
      animation: 'seg-glow 2s ease-in-out infinite',
    }
    if (state === 'dying') return {
      background: 'linear-gradient(180deg, #ef4444, #991b1b)',
      boxShadow: '0 0 8px rgba(239,68,68,0.4)',
      animation: 'dying-flash 1s ease-in-out infinite',
    }
    return { background: '#1a1a25', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }
  }

  return (
    <div style={{ display: 'inline-block', textAlign: 'center' }}>
      <style>{css}</style>

      {/* Top decorative border */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
        {[0, 0.15, 0.3].map((d, i) => (
          <div key={i} style={{ width: 4, height: 4, background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.6)', animation: `dot-blink 2s ease-in-out ${d}s infinite` }} />
        ))}
        <div style={{ width: 60, height: 2, background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.4), transparent)' }} />
        {[0.3, 0.15, 0].map((d, i) => (
          <div key={i} style={{ width: 4, height: 4, background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.6)', animation: `dot-blink 2s ease-in-out ${d}s infinite` }} />
        ))}
      </div>

      {/* LAST */}
      <div style={{
        fontFamily: cyberpunkTheme.fonts.display,
        fontSize: 'clamp(1rem, 3vw, 1.5rem)',
        color: '#c084fc',
        textShadow: '0 0 8px rgba(192,132,252,0.6), 0 0 25px rgba(192,132,252,0.3), 3px 3px 0 #1a0530',
        letterSpacing: '6px',
        lineHeight: 2,
      }}>LAST</div>

      {/* NAD + glitch */}
      <div style={{ position: 'relative', lineHeight: 1 }}>
        <div style={{
          fontFamily: cyberpunkTheme.fonts.display,
          fontSize: 'clamp(2.5rem, 8vw, 3.8rem)',
          color: '#00f5ff',
          letterSpacing: '10px',
          animation: 'nad-pulse 3s ease-in-out infinite',
          lineHeight: 1.2,
        }}>NAD</div>
        {/* glitch overlay */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          fontFamily: cyberpunkTheme.fonts.display,
          fontSize: 'clamp(2.5rem, 8vw, 3.8rem)',
          letterSpacing: '10px',
          color: '#ff006e',
          opacity: 0,
          pointerEvents: 'none',
          animation: 'nad-glitch 6s infinite',
          lineHeight: 1.2,
        }} aria-hidden="true">NAD</div>
      </div>

      {/* STANDING */}
      <div style={{
        fontFamily: cyberpunkTheme.fonts.display,
        fontSize: 'clamp(1rem, 3vw, 1.5rem)',
        color: '#c084fc',
        textShadow: '0 0 8px rgba(192,132,252,0.6), 0 0 25px rgba(192,132,252,0.3), 3px 3px 0 #1a0530',
        letterSpacing: '6px',
        lineHeight: 2,
      }}>STANDING</div>

      {/* Elimination bar */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 24 }}>
        {segs.map((seg, i) => (
          <div key={i} style={{
            width: 24, height: 10, borderRadius: 1,
            ...segStyle(seg.state),
            ...(seg.state === 'alive' && i % 2 === 1 ? { animationDelay: '0.4s' } : {}),
          }} />
        ))}
      </div>

      {/* Number grid */}
      <NumberGrid />

    </div>
  )
}

/* ─────────────────────────────────────────
   Pixel diamond logo (same as OG banner) — for header
───────────────────────────────────────── */
const PIXEL_DIAMOND_RECTS = getPixelDiamondRects()

const PixelDiamondLogo = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="-16 -16 32 32"
    style={{
      display: 'block',
      flexShrink: 0,
      filter: 'drop-shadow(0 0 6px rgba(147,51,234,0.4))',
      imageRendering: 'pixelated',
      overflow: 'visible',
    }}
  >
    {PIXEL_DIAMOND_RECTS.map(({ x, y, fill }, i) => (
      <rect key={i} x={x} y={y} width={1} height={1} fill={fill} />
    ))}
  </svg>
)

/* ─────────────────────────────────────────
   VARIATION C — Horizontal lockup (AppHeader)
───────────────────────────────────────── */
export function HeaderLogo() {
  return (
    <>
      <style>{`
        .header-diamond-wrap svg { display: block; }
        @media (max-width: 480px) {
          .header-logo-divider { display: none !important; }
          .header-logo-text { display: none !important; }
          .header-diamond-wrap { width: 40px !important; height: 40px !important; min-width: 40px !important; }
          .header-diamond-wrap svg { width: 40px !important; height: 40px !important; }
        }
      `}</style>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'clamp(6px, 1.5vw, 10px)', textDecoration: 'none', minWidth: 0 }}>
        {/* Pixel diamond (same as OG banner) — proportional square */}
        <div
          className="header-diamond-wrap"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PixelDiamondLogo size={40} />
        </div>

        {/* Divider — hidden on small mobile */}
        <div
          className="header-logo-divider"
          style={{ width: 1, height: 28, background: 'linear-gradient(180deg, transparent, rgba(168,85,247,0.4), transparent)', flexShrink: 0 }}
        />

        {/* Text lockup — hidden on small mobile */}
        <div className="header-logo-text" style={{ textAlign: 'left', overflow: 'hidden' }}>
          <div style={{
            fontFamily: cyberpunkTheme.fonts.display,
            fontSize: 'clamp(0.55rem, 1.6vw, 0.75rem)',
            color: '#c084fc',
            textShadow: '0 0 8px rgba(192,132,252,0.4), 2px 2px 0 #1a0530',
            letterSpacing: '1.5px',
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
          }}>
            LAST <span style={{ color: '#00f5ff', textShadow: '0 0 8px rgba(0,245,255,0.6), 2px 2px 0 #062a30' }}>NAD</span> STANDING
          </div>
          <div style={{
            fontFamily: cyberpunkTheme.fonts.body,
            fontSize: 'clamp(0.5rem, 1vw, 0.55rem)',
            color: '#4a4a6a',
            letterSpacing: '1.5px',
            marginTop: '2px',
            whiteSpace: 'nowrap',
          }}>
            ON <span style={{ color: '#c084fc', textShadow: '0 0 6px rgba(192,132,252,0.4)' }}>MONAD</span> NETWORK
          </div>
        </div>
      </div>
    </>
  )
}

/* ─────────────────────────────────────────
   VARIATION B — Compact icon
───────────────────────────────────────── */
function IconLogo() {
  return (
    <div style={{
      width: 44, height: 44,
      border: `2px solid rgba(168, 85, 247, 0.3)`,
      borderRadius: '6px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(168, 85, 247, 0.05)',
      gap: '4px',
    }}>
      <span style={{
        fontFamily: cyberpunkTheme.fonts.display,
        fontSize: '0.75rem',
        color: '#00f5ff',
        textShadow: '0 0 8px rgba(0,245,255,0.7)',
      }}>NAD</span>
      <div style={{ display: 'flex', gap: 2 }}>
        {[1,1,1,1,1,0,0].map((on, i) => (
          <div key={i} style={{
            width: 6, height: 3, borderRadius: 1,
            background: on ? '#a855f7' : '#1a1a25',
            boxShadow: on ? '0 0 4px rgba(168,85,247,0.5)' : 'none',
          }} />
        ))}
      </div>
    </div>
  )
}
